/**
 * The onboarding wizard's wording lives in the locale files under `learnerOb.*`; this module
 * only maps the option ids (from modules/studyPlan, mirrored from @examing/types) to those keys
 * and holds the icons. The Telegram Mini App asks the same questions with the Uzbek wording.
 */
import type { LucideIcon } from 'lucide-react';
import { Award, BookOpen, Clock, Flame, GraduationCap, Languages, Rocket, Zap } from 'lucide-react';

/** i18n keys for an option's title and, where it has one, its subtitle. */
export interface OptionKeys {
  label: string;
  sub?: string;
}

const P = 'learnerOb.';
const withSub = (base: string): OptionKeys => ({ label: `${P}${base}`, sub: `${P}${base}_sub` });

export const EXAM_ICON: Readonly<Record<string, LucideIcon>> = {
  DTM: GraduationCap,
  MILLIY_SERTIFIKAT: Award,
  IELTS: Languages,
  SAT: Rocket,
  ATTESTATSIYA: BookOpen,
  LANGUAGES: Languages,
};

export const MINUTES_ICON: Readonly<Record<number, LucideIcon>> = { 30: Zap, 60: Clock, 120: Flame, 180: Rocket };

export const examKeys = (exam: string): OptionKeys => withSub(`exam_${exam}`);
export const ageKeys = (age: string): OptionKeys => withSub(`age_${age}`);
export const minutesKeys = (minutes: number): OptionKeys => withSub(`minutes_${minutes}`);
export const timeframeKeys = (timeframe: string): OptionKeys => withSub(`timeframe_${timeframe}`);

/** These exams' levels read differently per exam and carry a subtitle (a score range, a CEFR level). */
const EXAMS_WITH_LEVEL_SUBTITLES = new Set(['MILLIY_SERTIFIKAT', 'IELTS', 'SAT']);

/** Grades and attestation categories are one title; Milliy Sertifikat grades, IELTS bands and SAT levels are per exam. */
export const levelKeys = (exam: string, level: string): OptionKeys =>
  EXAMS_WITH_LEVEL_SUBTITLES.has(exam) ? withSub(`level_${exam}_${level}`) : { label: `${P}level_${level}` };

/** As languages to learn, the same ids read as the language alone. */
export const subjectKey = (exam: string, subject: string): string =>
  exam === 'LANGUAGES' ? `${P}language_${subject}` : `${P}subject_${subject}`;

/** The questions on an exam's own screen, as i18n keys. */
export const GOAL_QUESTIONS: Readonly<Record<string, { level?: string; subjects?: string; hint?: string }>> = {
  DTM: { level: `${P}q_DTM_level`, subjects: `${P}q_DTM_subjects` },
  MILLIY_SERTIFIKAT: {
    subjects: `${P}q_MILLIY_SERTIFIKAT_subjects`,
    level: `${P}q_MILLIY_SERTIFIKAT_level`,
    hint: `${P}hint_multiple`,
  },
  IELTS: { level: `${P}q_IELTS_level` },
  SAT: { level: `${P}q_SAT_level` },
  ATTESTATSIYA: {
    subjects: `${P}q_ATTESTATSIYA_subjects`,
    hint: `${P}q_ATTESTATSIYA_hint`,
    level: `${P}q_ATTESTATSIYA_level`,
  },
  LANGUAGES: { subjects: `${P}q_LANGUAGES_subjects` },
};
